import React from 'react';
import './Pages/PagesCss/LegalInformation.css';

const PrivacyPolicy = () => {
  return (
    <div className="privacy-policy">
      {/* Header Section */}
      <section className="policy-header">
        <div className="container">
          <div className="header-content">
            <h1>Privacy Policy</h1>
            <p className="last-updated">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            <p className="header-description">
              At Font-Max, we take your privacy seriously. This policy explains how we collect, use, 
              and protect your information when you use our text processing and font generation tools.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="policy-content">
        <div className="container-one">
          <div className="policy-grid">
            {/* Table of Contents */}
            <aside className="table-of-contents">
              <h3>Table of Contents</h3>
              <div>
                <ul>
                  <li><a href="#information-collection">Information We Collect</a></li>
                  <li><a href="#information-use">How We Use Information</a></li>
                  <li><a href="#information-sharing">Information Sharing</a></li>
                  <li><a href="#data-security">Data Security</a></li>
                  <li><a href="#cookies">Cookies & Tracking</a></li>
                  <li><a href="#user-rights">Your Rights</a></li>
                  <li><a href="#children-privacy">Children's Privacy</a></li>
                  <li><a href="#policy-changes">Policy Changes</a></li>
                  <li><a href="#contact-us">Contact Us</a></li>
                </ul>
              </div>
            </aside>

            {/* Policy Sections */}
            <div className="policy-sections">
              <section id="information-collection" className="policy-section">
                <h2>1. Information We Collect</h2>
                <p>
                  This Privacy Policy applies to all websites and subdomains operated by D-Tech Studios for Font-Max, including <a href="https://font-max.com" target="_blank" rel="noopener noreferrer">https://font-max.com</a>.
                </p>

                <div className="section-content">
                  <h3>1.1 Personal Information</h3>
                  <p>We collect information that you voluntarily provide to us when you:</p>
                  <ul>
                    <li>Use our text processing tools and font generators</li>
                    <li>Contact us through our website</li>
                    <li>Subscribe to our newsletter</li>
                    <li>Create an account (if applicable)</li>
                  </ul>

                  <h3>1.2 Automatically Collected Information</h3>
                  <p>When you visit our website, we automatically collect certain information including:</p>
                  <ul>
                    <li>IP address and browser type</li>
                    <li>Device information and screen resolution</li>
                    <li>Usage data and interaction with our tools</li>
                    <li>Cookies and similar tracking technologies</li>
                    <li>Referring website and search queries</li>
                  </ul>

                  <h3>1.3 Text Data Processing</h3>
                  <p>
                    <strong>Important:</strong> All text processing (font generation, text conversion, encoding, etc.) 
                    is performed locally in your browser whenever possible. We do not store or transmit 
                    your input text to our servers unless necessary for specific processing requirements. 
                    Your text content remains private and is not permanently stored on our systems.
                  </p>
                </div>
              </section>

              <section id="information-use" className="policy-section">
                <h2>2. How We Use Your Information</h2>
                <div className="section-content">
                  <p>We use the information we collect to:</p>
                  <ul>
                    <li>Provide and maintain our text processing and font generation services</li>
                    <li>Improve and optimize our tools and user experience</li>
                    <li>Respond to your inquiries and provide customer support</li>
                    <li>Send you updates about new tools and features (if subscribed)</li>
                    <li>Analyze usage patterns to enhance our services</li>
                    <li>Ensure the security and integrity of our platform</li>
                    <li>Develop new text processing tools based on user needs</li>
                  </ul>
                </div>
              </section>

              <section id="information-sharing" className="policy-section">
                <h2>3. Information Sharing and Disclosure</h2>
                <div className="section-content">
                  <p>We do not sell, trade, or rent your personal information or text content to third parties. We may share information only in the following circumstances:</p>
                  
                  <h3>3.1 Service Providers</h3>
                  <p>We may employ third-party companies to facilitate our service, provide the service on our behalf, or assist us in analyzing how our service is used.</p>

                  <h3>3.2 Legal Requirements</h3>
                  <p>We may disclose your information if required to do so by law or in response to valid requests by public authorities.</p>

                  <h3>3.3 Business Transfers</h3>
                  <p>In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.</p>

                  <h3>3.4 Analytics and Performance</h3>
                  <p>We may share aggregated, non-personally identifiable information with analytics providers to help us understand tool usage patterns.</p>
                </div>
              </section>

              <section id="data-security" className="policy-section">
                <h2>4. Data Security</h2>
                <div className="section-content">
                  <p>We implement appropriate technical and organizational security measures to protect your personal information. However, please note that no method of transmission over the Internet or electronic storage is 100% secure.</p>
                  
                  <div className="security-features">
                    <h3>Our Security Measures Include:</h3>
                    <ul>
                      <li>SSL encryption for all data transmissions</li>
                      <li>Regular security assessments and updates</li>
                      <li>Limited access to personal information on a need-to-know basis</li>
                      <li>Secure server infrastructure</li>
                      <li>Local processing of sensitive text data when possible</li>
                      <li>Regular data purging of temporary processing files</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section id="cookies" className="policy-section">
                <h2>5. Cookies and Tracking Technologies</h2>
                <div className="section-content">
                  <p>We use cookies and similar tracking technologies to track activity on our service and hold certain information.</p>

                  <p>
                    We may use third-party services such as Google Analytics or advertising partners to improve our services. 
                    These services may collect anonymized data about your interactions with our websites.
                  </p>
                  
                  <h3>Types of Cookies We Use:</h3>
                  <ul>
                    <li><strong>Essential Cookies:</strong> Necessary for the website to function properly</li>
                    <li><strong>Analytics Cookies:</strong> Help us understand how visitors interact with our tools</li>
                    <li><strong>Preference Cookies:</strong> Remember your tool settings and preferences</li>
                    <li><strong>Performance Cookies:</strong> Monitor tool performance and loading times</li>
                  </ul>

                  <p>You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our service.</p>
                </div>
              </section>

              <section id="user-rights" className="policy-section">
                <h2>6. Your Data Protection Rights</h2>
                <div className="section-content">
                  <p>Depending on your location, you may have the following rights regarding your personal information:</p>
                  
                  <div className="rights-grid">
                    <div className="right-item">
                      <h4>Access</h4>
                      <p>Request copies of your personal data</p>
                    </div>
                    <div className="right-item">
                      <h4>Rectification</h4>
                      <p>Request correction of inaccurate information</p>
                    </div>
                    <div className="right-item">
                      <h4>Erasure</h4>
                      <p>Request deletion of your personal data</p>
                    </div>
                    <div className="right-item">
                      <h4>Restriction</h4>
                      <p>Request restriction of processing your data</p>
                    </div>
                    <div className="right-item">
                      <h4>Portability</h4>
                      <p>Request transfer of your data to another organization</p>
                    </div>
                    <div className="right-item">
                      <h4>Objection</h4>
                      <p>Object to our processing of your personal data</p>
                    </div>
                  </div>
                  
                  <p className="rights-notice">
                    To exercise any of these rights, please contact us using the information provided in the "Contact Us" section.
                  </p>
                </div>
              </section>

              <section id="children-privacy" className="policy-section">
                <h2>7. Children's Privacy</h2>
                <div className="section-content">
                  <p>
                    Our service is not intended for individuals under the age of 18. We do not knowingly 
                    collect personally identifiable information from children under 18. If you are a parent 
                    or guardian and you are aware that your child has provided us with personal information, 
                    please contact us.
                  </p>
                </div>
              </section>

              <section id="policy-changes" className="policy-section">
                <h2>8. Changes to This Privacy Policy</h2>
                <div className="section-content">
                  <p>
                    We may update our Privacy Policy from time to time. We will notify you of any changes 
                    by posting the new Privacy Policy on this page and updating the "Last updated" date.
                  </p>
                  <p>
                    You are advised to review this Privacy Policy periodically for any changes. Changes 
                    to this Privacy Policy are effective when they are posted on this page.
                  </p>
                </div>
              </section>

              <section id="contact-us" className="policy-section">
                <h2>9. Contact Us</h2>
                <div className="section-content">
                  <p>If you have any questions about this Privacy Policy, please contact us:</p>
                  <div className="contact-info">
                    <div className="contact-item">
                      <strong>Email:</strong> support@font-max.com
                    </div>
                    <div className="contact-item">
                      <strong>Address:</strong> 2568 Madison Ave, League City, TX 77573
                    </div>
                    <div className="contact-item">
                      <strong>Website:</strong> <a href="https://font-max.com">https://font-max.com</a>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="policy-actions">
        <div className="container">
          <div className="actions-content">
            <h3>Need Help?</h3>
            <p>If you have any questions about our privacy practices or your personal data, we're here to help.</p>
            <div className="action-buttons">
              <a href="/Contact" className="btn btn-primary">Contact Us</a>
              <a href="/FontGenerator" className="btn btn-secondary">Try Our Tools</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;